import { imageRule } from "@opulancia/renderer/editorial/instructions";
import { visualBriefLines } from "@opulancia/renderer/images/visual-style";

/**
 * One slide's image brief as plain text, to paste where the image is made or searched for:
 * the scenario's brief, then the calibrated F1 rules for that slide's role. The Atelier makes
 * no image; this only carries the brief.
 */
export interface BriefContext {
  preparationId: string;
  /** Position in the project (1-based). */
  slide: number;
  role: string;
  title: string;
  sub: string;
  brief: Record<string, unknown> | null;
}

const FIELDS: Array<[string, string]> = [
  ["shows", "Montre"],
  ["subject", "Sujet"],
  ["why", "Pourquoi"],
  ["composition", "Composition"],
  ["negative_space", "Espace pour le texte"],
  ["realism", "Réalisme"],
  ["light", "Lumière"],
  ["framing", "Cadrage"],
  ["viewpoint", "Point de vue"],
  ["texture", "Matières"],
];

export function briefForCopy(c: BriefContext): string {
  const role = c.role === "hook" ? "cover" : c.role === "cta" ? "end" : "inner";
  const list = (key: string) => (Array.isArray(c.brief?.[key]) ? (c.brief![key] as unknown[]).filter((v): v is string => typeof v === "string") : []);
  const lines = [
    `Brief d'image — slide ${c.slide} (${c.role}) — préparation ${c.preparationId}, format F1 « liste commentée »`,
    "",
    `Texte posé par l'Atelier sur l'image (ne pas l'écrire dans l'image) : « ${c.title}${c.sub ? ` / ${c.sub}` : ""} »`,
    "",
  ];
  if (c.brief) {
    for (const [key, label] of FIELDS) if (typeof c.brief[key] === "string" && c.brief[key]) lines.push(`${label} : ${String(c.brief[key])}`);
    if (list("constraints").length) lines.push(`Contraintes : ${list("constraints").join(" ; ")}`);
    if (list("avoid").length) lines.push(`À éviter : ${list("avoid").join(" ; ")}`);
  } else lines.push("Aucun brief d'image dans le scénario pour cette slide.");
  lines.push("", imageRule("representation", "fr"), imageRule("facts", "fr"));
  lines.push("", "Repères calibrés sur la bibliothèque (F1, des repères, pas des contraintes) :", ...visualBriefLines({ format: "F1", role, hasText: !!c.title.trim() }).map((l) => `- ${l}`));
  lines.push("", "Slide de 1080 × 1350 px (4:5) : l'image est recadrée dans l'Atelier, prévoir de la marge autour du sujet.");
  return lines.join("\n");
}

/** A cover concept's brief as plain text: the concept, its mechanism and surprise, the sense-first brief, then the observed references. */
export function coverBriefForCopy(c: {
  preparationId: string;
  concept: number;
  title: string;
  hook: { visual_concept: string; mechanism?: string; surprise?: string; cover_brief?: Record<string, string | undefined> };
  mechanism: string | null;
}): string {
  const b = c.hook.cover_brief ?? {};
  const field = (label: string, key: string) => (b[key] ? [`${label} : ${b[key]}`] : []);
  return [
    `Brief de couverture — concept ${c.concept} — préparation ${c.preparationId}, format F1 « liste commentée »`,
    "",
    `Titre posé par l'Atelier (ne pas l'écrire dans l'image) : « ${c.title} »`,
    `Concept visuel : ${c.hook.visual_concept}`,
    ...(c.mechanism ? [`Mécanisme : ${c.hook.mechanism} — ${c.mechanism}`] : []),
    ...(c.hook.surprise ? [`Surprise : ${c.hook.surprise}`] : []),
    "",
    "Le sens d'abord :",
    ...field("Question que l'image pose seule", "visual_question"),
    ...field("Anomalie visible", "anomaly"),
    ...field("Lien avec le titre", "title_link"),
    ...field("Fidélité (ne pas exagérer)", "fidelity"),
    ...(b["visual_question"] || b["anomaly"] ? [] : ["(non précisé dans la réponse : à décider avant de faire l'image)"]),
    "",
    "Puis l'image :",
    ...field("Sujet", "subject"),
    ...field("Situation", "situation"),
    ...field("Cadrage", "framing"),
    ...field("Lumière", "light"),
    ...field("Couleurs", "colors"),
    ...field("Zone du titre et du logo", "text_zone"),
    ...field("Référence OPULANCIA", "reference"),
    "",
    imageRule("reading", "fr"),
    imageRule("representation", "fr"),
    imageRule("facts", "fr"),
    "",
    "Place : ce qui porte l'accroche (l'anomalie) reste visible au-dessus du tiers bas, où l'Atelier pose le titre, le dégradé et la flèche ; le haut reste calme pour le logo.",
    "",
    "Repères observés dans la bibliothèque (des repères, pas des contraintes) :",
    ...visualBriefLines({ format: "F1", role: "cover", hasText: true }).map((l) => `- ${l}`),
    "",
    "Libre : couleur, lumière, cadrage et décor suivent le sujet. Fixes : la Signature (Anton / Inter, ocre #E0A84A, logo) et la crédibilité. Aucun texte, chiffre ni logo dans l'image.",
  ].join("\n");
}
